import { moveToFulfilled, moveToPending, moveToRejected, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  GroupEmailOptionsLoadAction,
  GroupEmailOptionsSubscribeToEmailAction,
  GroupEmailOptionsUnsubscribeFromEmailAction,
} from '../../domains/groupEmailOptions/actions.types';
import type { Group } from '../groups/types';

import type { GroupsEmailOptions, ViewerSubscriptions } from './types';

type GroupEmailOptionsUpdateEmailActions =
  | GroupEmailOptionsSubscribeToEmailAction
  | GroupEmailOptionsUnsubscribeFromEmailAction;

export type GroupEmailOptionsActions = GroupEmailOptionsLoadAction | GroupEmailOptionsUpdateEmailActions;

const initialState: GroupsEmailOptions = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const shallowCloneArray = (array: ViewerSubscriptions) => [...array];

type UpdateEmailSubscription = (state: GroupsEmailOptions, groupId: Group['id']) => ViewerSubscriptions;

const subscribeToEmail: UpdateEmailSubscription = (state, groupId) => {
  const fulfilledEmailOptions = state.fulfilled.byId[groupId];
  const viewerSubscriptions = fulfilledEmailOptions?.viewerSubscriptions
    ? shallowCloneArray(fulfilledEmailOptions.viewerSubscriptions)
    : [];

  if (!viewerSubscriptions.includes('EMAIL')) {
    return [...viewerSubscriptions, 'EMAIL'];
  }

  return viewerSubscriptions;
};

const unsubscribeFromEmail: UpdateEmailSubscription = (state, groupId) => {
  const fulfilledEmailOptions = state.fulfilled.byId[groupId];
  const viewerSubscriptions = fulfilledEmailOptions?.viewerSubscriptions || [];

  return [...viewerSubscriptions.filter((item) => item !== 'EMAIL')];
};

const reducer: Reducer<GroupsEmailOptions, GroupEmailOptionsActions> = (state = initialState, action) => {
  switch (action.type) {
    case 'GROUP_EMAIL_OPTIONS.LOAD_PENDING':
      return moveToPending(state, [action.payload.groupId]);
    case 'GROUP_EMAIL_OPTIONS.LOAD_FULFILLED':
      return moveToFulfilled(state, { [action.groupId]: { id: action.groupId, ...action.payload } });
    case 'GROUP_EMAIL_OPTIONS.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.groupId], action.payload.error);
    case 'GROUP_EMAIL_OPTIONS.SUBSCRIBE_PENDING':
      return update(state, action.payload.groupId, {
        viewerSubscriptions: subscribeToEmail(state, action.payload.groupId),
      });
    case 'GROUP_EMAIL_OPTIONS.SUBSCRIBE_FULFILLED':
      return moveToFulfilled(state, {
        [action.payload.id]: {
          ...state.fulfilled.byId[action.payload.id],
          id: action.payload.id,
          viewerSubscriptions: subscribeToEmail(state, action.payload.id),
        },
      });
    case 'GROUP_EMAIL_OPTIONS.SUBSCRIBE_REJECTED':
      return update(state, action.payload.groupId, {
        viewerSubscriptions: unsubscribeFromEmail(state, action.payload.groupId),
      });
    case 'GROUP_EMAIL_OPTIONS.UNSUBSCRIBE_PENDING':
      return update(state, action.payload.groupId, {
        viewerSubscriptions: unsubscribeFromEmail(state, action.payload.groupId),
      });
    case 'GROUP_EMAIL_OPTIONS.UNSUBSCRIBE_FULFILLED':
      return moveToFulfilled(state, {
        [action.payload.id]: {
          ...state.fulfilled.byId[action.payload.id],
          id: action.payload.id,
          viewerSubscriptions: unsubscribeFromEmail(state, action.payload.id),
        },
      });
    case 'GROUP_EMAIL_OPTIONS.UNSUBSCRIBE_REJECTED':
      return update(state, action.payload.groupId, {
        viewerSubscriptions: subscribeToEmail(state, action.payload.groupId),
      });
    default:
      return state;
  }
};

export default reducer;
